import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiRequest } from '../utils/api';
import ScannerAutomation from '../components/ScannerAutomation';

const STRATEGY_OPTIONS = [
  { value: 'all', label: 'All Positions' },
  { value: 'Iron Condor', label: 'Iron Condor Only' },
  { value: 'Credit Put Spread', label: 'Credit Put Spread Only' }
];

const fmtNum = (n, digits = 2) =>
  n == null || !Number.isFinite(Number(n)) ? '-' : Number(n).toFixed(digits);
const fmtMoney = (n) =>
  n == null || !Number.isFinite(Number(n))
    ? '-'
    : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Color + variant for the actionable strategy badge.
const strategyBadge = (rec) => {
  switch (rec) {
    case 'Iron Condor':
      return { color: 'secondary', variant: 'filled' };
    case 'Credit Put Spread':
      return { color: 'success', variant: 'filled' };
    default:
      return { color: 'default', variant: 'outlined' };
  }
};

const Scanner = () => {
  const [tab, setTab] = useState(0);
  const [rawResults, setRawResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [earningsConfigured, setEarningsConfigured] = useState(true);
  const [scannedAt, setScannedAt] = useState(null);

  // Local interactive control states (filter without re-calling the API).
  const [minDte, setMinDte] = useState(20);
  const [maxDte, setMaxDte] = useState(50);
  const [excludeEarnings, setExcludeEarnings] = useState(true);
  const [strategy, setStrategy] = useState('all');

  // minDte/maxDte drive the backend chain selection, so the fetch depends on them.
  const fetchScan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiRequest(`/api/scanner?minDte=${minDte}&maxDte=${maxDte}`);
      const data = resp.data || {};
      setRawResults(Array.isArray(data.results) ? data.results : []);
      setEarningsConfigured(data.earningsConfigured !== false);
      setScannedAt(data.scannedAt || null);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to run scanner');
      setRawResults([]);
    } finally {
      setLoading(false);
    }
  }, [minDte, maxDte]);

  useEffect(() => {
    fetchScan();
  }, [fetchScan]);

  // Does the ticker have at least one expiration inside the DTE window?
  const hasExpirationInWindow = useCallback(
    (row) => {
      const today = new Date(new Date().toISOString().split('T')[0]).getTime();
      const dtes = (row.expirationDates || []).map((d) =>
        Math.round((new Date(d).getTime() - today) / (24 * 60 * 60 * 1000))
      );
      return dtes.some((dte) => dte >= minDte && dte <= maxDte);
    },
    [minDte, maxDte]
  );

  // Apply all local filters to the raw scan results.
  const filteredResults = useMemo(() => {
    return rawResults.filter((row) => {
      // Dynamic expiration window filter: hide tickers lacking a cycle in range.
      if ((row.expirationDates || []).length > 0 && !hasExpirationInWindow(row)) return false;

      // Binary catalyst buffer: hide if earnings land inside the exposure window.
      if (excludeEarnings && row.daysToEarnings != null && row.daysToEarnings <= maxDte) {
        return false;
      }

      // Strategy matching dropdown.
      if (strategy !== 'all' && row.recommendation !== strategy) return false;

      return true;
    });
  }, [rawResults, hasExpirationInWindow, excludeEarnings, maxDte, strategy]);

  const matchCount = filteredResults.filter((r) => r.recommendation !== 'Hold / Wait').length;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Scans your watchlist via Tradier for range-bound (Iron Condor) and bullish-rebound (Credit Put Spread) setups.
            {scannedAt && ` Last scan: ${new Date(scannedAt).toLocaleString()}`}
          </Typography>
        </Box>
        {tab === 0 && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchScan}
            disabled={loading}
          >
            Rescan
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Scan" />
        <Tab label="Automation" />
      </Tabs>

      {tab === 1 && <ScannerAutomation />}

      {tab === 0 && (
      <>
      {!earningsConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Earnings calendar is not configured. Set <code>FINNHUB_API_KEY</code> on the server to enable the
          days-to-earnings catalyst filter. Earnings columns will show "N/A" until then.
        </Alert>
      )}

      {/* Frontend Interactive Control Filter bar */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>DTE Range:</Typography>
              <TextField
                label="Min"
                type="number"
                size="small"
                value={minDte}
                onChange={(e) => setMinDte(Math.max(0, parseInt(e.target.value, 10) || 0))}
                sx={{ width: 90 }}
              />
              <Typography variant="body2">to</Typography>
              <TextField
                label="Max"
                type="number"
                size="small"
                value={maxDte}
                onChange={(e) => setMaxDte(Math.max(0, parseInt(e.target.value, 10) || 0))}
                sx={{ width: 90 }}
              />
            </Box>

            <FormControlLabel
              control={<Checkbox checked={excludeEarnings} onChange={(e) => setExcludeEarnings(e.target.checked)} />}
              label="Exclude tickers with earnings in window"
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="strategy-label">Strategy</InputLabel>
              <Select
                labelId="strategy-label"
                label="Strategy"
                value={strategy}
                onChange={(e) => setStrategy(e.target.value)}
              >
                {STRATEGY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />
            <Chip
              label={`${matchCount} match${matchCount === 1 ? '' : 'es'} / ${filteredResults.length} shown`}
              color={matchCount > 0 ? 'primary' : 'default'}
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Ticker</TableCell>
                <TableCell align="right">Last</TableCell>
                <TableCell align="right">20 SMA</TableCell>
                <TableCell align="right">200 EMA</TableCell>
                <TableCell align="right">RSI(14)</TableCell>
                <TableCell align="right">IV</TableCell>
                <TableCell align="right">IV Rank</TableCell>
                <TableCell align="center">Earnings</TableCell>
                <TableCell align="center">Strategy</TableCell>
                <TableCell>Target Boundaries</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {rawResults.length === 0
                      ? 'No watchlist tickers to scan, or no data returned.'
                      : 'No tickers match the current filters.'}
                  </TableCell>
                </TableRow>
              )}
              {filteredResults.map((row) => {
                const badge = strategyBadge(row.recommendation);
                const earningsInWindow =
                  row.daysToEarnings != null && row.daysToEarnings <= maxDte;
                const rsiColor =
                  row.rsi14 == null ? 'inherit' : row.rsi14 < 35 ? 'error.main' : row.rsi14 > 60 ? 'warning.main' : 'success.main';
                const ivRankColor =
                  row.ivRank == null ? 'inherit' : row.ivRank > 35 ? 'success.main' : 'text.secondary';
                return (
                  <TableRow key={row.symbol} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{row.symbol}</Typography>
                      {row.context && (
                        <Tooltip title={row.context}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.context}
                          </Typography>
                        </Tooltip>
                      )}
                      {row.error && (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                          {row.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{fmtMoney(row.lastClose)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.sma20)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.ema200)}</TableCell>
                    <TableCell align="right" sx={{ color: rsiColor, fontWeight: 600 }}>
                      {fmtNum(row.rsi14, 0)}
                    </TableCell>
                    <TableCell align="right">{row.iv == null ? '-' : `${fmtNum(row.iv, 1)}%`}</TableCell>
                    <TableCell align="right" sx={{ color: ivRankColor, fontWeight: 600 }}>
                      {row.ivRank == null ? '-' : `${fmtNum(row.ivRank, 0)}%`}
                    </TableCell>
                    <TableCell align="center">
                      {row.daysToEarnings == null ? (
                        <Typography variant="caption" color="text.secondary">N/A</Typography>
                      ) : (
                        <Chip
                          size="small"
                          icon={earningsInWindow ? <WarningAmberIcon /> : undefined}
                          label={`${row.daysToEarnings}d`}
                          color={earningsInWindow ? 'error' : 'default'}
                          variant={earningsInWindow ? 'filled' : 'outlined'}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={row.recommendation}
                        color={badge.color}
                        variant={badge.variant}
                      />
                    </TableCell>
                    <TableCell>
                      {row.boundaries ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          {row.boundaries.shortPut != null && (
                            <Typography variant="caption">
                              Short Put: <b>{row.boundaries.shortPut}</b>
                              <span style={{ color: '#888' }}> (≈S2 {fmtNum(row.boundaries.pivotS2)})</span>
                            </Typography>
                          )}
                          {row.boundaries.shortCall != null && (
                            <Typography variant="caption">
                              Short Call: <b>{row.boundaries.shortCall}</b>
                              <span style={{ color: '#888' }}> (≈R2 {fmtNum(row.boundaries.pivotR2)})</span>
                            </Typography>
                          )}
                          {row.evaluatedExpiration && (
                            <Typography variant="caption" color="text.secondary">
                              Exp {row.evaluatedExpiration} ({row.evaluatedDte}d)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {row.recommendation === 'Hold / Wait' ? 'No high-probability setup' : '-'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>
      )}
    </Box>
  );
};

export default Scanner;
